import { Injectable } from "@angular/core";
import { firstValueFrom, iif, of, Subject } from "rxjs";
import { delay, switchMap, takeUntil } from "rxjs/operators";
import { FmsService } from "src/app/services/fms.service";
import { FetchPrognosesRunningJobsService } from "src/app/services/running-jobs/fetch-prognoses-running-jobs.service";
import { ToastService } from "src/app/services/toast.service";

const DISPLAY_SNACKBAR_THRESHOLD = 0;

@Injectable({
  providedIn: "root",
})
export class FetchPrognosesSnackBarService {
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly fetchPrognosesRunningJobsService: FetchPrognosesRunningJobsService,
    private readonly fmsService: FmsService,
    private readonly toastService: ToastService,
  ) {}

  public openSnackBarOnCurrentYearLoading(): void {
    this.fetchPrognosesRunningJobsService.isCurrentYearLoaded$
      .pipe(
        /**
         * Prevent snack bar from being displayed when prognoses are loaded quickly
         *
         * switchMap() with delay() will stop all emissions when
         * isCurrentYearLoaded goes false -> true before the delay has expired,
         * and consequently prevent the snack bar from being displayed
         */
        switchMap((isCurrentYearLoaded) =>
          iif(
            () => isCurrentYearLoaded,
            of(isCurrentYearLoaded),
            of(isCurrentYearLoaded).pipe(delay(DISPLAY_SNACKBAR_THRESHOLD)),
          ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((isCurrentYearLoaded) => (isCurrentYearLoaded ? this.close() : this.open()));
  }

  public destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.close();
  }

  private async open(): Promise<void> {
    const text = await firstValueFrom(this.fmsService.translateAsync<string>("pensionPlan.snackBar.loading"));
    this.toastService.show({ text, duration: "always" });
  }

  private close(): void {
    this.toastService.hide();
  }
}
